import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Mic, Pause, Play } from 'lucide-react';

/** Format seconds as m:ss */
function fmt(s) {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * VoiceNotePlayer — polished audio player for voice-note attachments.
 * Props: url, duration (seconds, optional hint), tone: 'light' | 'dark'
 */
export default function VoiceNotePlayer({ url, duration: hintedDuration, tone = 'light' }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(hintedDuration || 0);
  const [playbackError, setPlaybackError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onTime = () => setCurrent(audio.currentTime || 0);
    const onEnd = () => {
      setPlaying(false);
      setCurrent(0);
    };
    const onPause = () => setPlaying(false);
    const onPlay = () => {
      setPlaybackError(false);
      setPlaying(true);
    };
    const onMeta = () => {
      if (!Number.isFinite(audio.duration) || audio.duration === Infinity) return;
      setDuration(audio.duration);
    };
    const onError = () => {
      setPlaying(false);
      setPlaybackError(true);
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('error', onError);
    };
  }, [url]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio || !url) return;

    try {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        setPlaybackError(false);
        await audio.play();
        setPlaying(true);
      }
    } catch {
      setPlaying(false);
      setPlaybackError(true);
    }
  };

  const progress = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const isDark = tone === 'dark';

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-2xl min-w-[200px] max-w-[280px] ${
        isDark ? 'bg-white/15 text-white' : 'bg-theme-surface text-theme-text'
      }`}
      data-testid="voice-note-player"
    >
      <button
        type="button"
        onClick={toggle}
        disabled={!url}
        className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${
          isDark ? 'bg-white/90 text-[#002FA7] hover:bg-white' : 'bg-[#002FA7] text-white hover:bg-[#0055FF]'
        }`}
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
        data-testid="voice-note-toggle"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Mic className={`h-3.5 w-3.5 ${isDark ? 'text-white/80' : 'text-[#002FA7]'}`} />
          <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Voice note</span>
          {playbackError && (
            <AlertCircle
              className="h-3.5 w-3.5 text-rose-500"
              aria-label="Voice note playback failed"
              data-testid="voice-note-error"
            />
          )}
        </div>

        <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/25' : 'bg-theme-muted/25'}`}>
          <div
            className={`h-full transition-[width] duration-75 ${isDark ? 'bg-white' : 'bg-[#002FA7]'}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex justify-between items-center mt-1">
          <span className="text-[10px] tabular-nums opacity-80">{fmt(current)}</span>
          <span className="text-[10px] tabular-nums opacity-80">{fmt(duration || hintedDuration || 0)}</span>
        </div>

        {playbackError && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-1 inline-block text-[10px] underline ${isDark ? 'text-white/80' : 'text-rose-500'}`}
            data-testid="voice-note-open-file"
          >
            Open audio
          </a>
        )}
      </div>

      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        playsInline
      />
    </div>
  );
}
